/**
 * @file Lead detail view — full lead info, contact actions, notes, action checklist
 */

import {
  getLeadById, updateLead, updateLeadStatus, addLeadNote, toggleLeadAction,
  moveLeadToPipeline, deleteLead,
  STATUS_OPTIONS, ACTION_OPTIONS, SOURCE_OPTIONS, timeAgo
} from '@services/leads.js';
import { pop } from '@core/router.js';
import { toast } from '@components/toast.js';
import { showModal, closeModal } from '@components/modal.js';

function render({ leadId }) {
  const lead = getLeadById(leadId);
  if (!lead) {
    return `
      <button class="back-btn" id="detail-back">\u2190 Back</button>
      <div class="empty-state">
        <div class="empty-state-icon">\u26A0\uFE0F</div>
        <div class="empty-state-text">Lead not found</div>
      </div>
    `;
  }

  const statusObj = STATUS_OPTIONS[lead.status] || STATUS_OPTIONS['new'];
  const badgeClass = statusObj.color || 'badge-gray';
  const contacted = timeAgo(lead.lastContacted || lead.updatedAt);

  // Parse notes for display (Deploy Z stores notes as a string)
  const notesLines = (lead.notes || '').split('\n').filter(l => l.trim());

  // Build activity timeline from activityLog
  const activities = (lead.activityLog || []).slice().reverse();

  return `
    <button class="back-btn" id="detail-back">\u2190 Back</button>

    <div class="card mb-md">
      <div class="flex-between mb-sm">
        <h2 style="flex:1;min-width:0;overflow:hidden;text-overflow:ellipsis;">${esc(lead.name || 'Unnamed Lead')}</h2>
        <span class="badge ${badgeClass}">${statusObj.label}</span>
      </div>
      ${lead.value ? `<div class="text-gold" style="font-size:1.2rem;font-weight:700;">$${Number(lead.value).toLocaleString()}</div>` : ''}
      ${lead.inPipeline ? '<div class="badge badge-blue" style="margin-top:6px;">In Pipeline</div>' : ''}
    </div>

    <!-- Contact Section -->
    <div class="detail-section">
      <div class="detail-section-header">Contact</div>
      <div class="detail-section-content">
        ${lead.phone ? `
          <div class="lead-contact-row">
            <a href="tel:${esc(lead.phone)}" class="lead-contact-link lead-contact-call">
              <span class="lead-contact-icon">\uD83D\uDCDE</span>
              <span class="lead-contact-text">${esc(lead.phone)}</span>
            </a>
            <a href="sms:${esc(lead.phone)}" class="lead-contact-action" title="Text">
              \u2709\uFE0F
            </a>
          </div>
        ` : ''}
        ${lead.email ? `
          <div class="lead-contact-row">
            <a href="mailto:${esc(lead.email)}" class="lead-contact-link">
              <span class="lead-contact-icon">\u2709</span>
              <span class="lead-contact-text">${esc(lead.email)}</span>
            </a>
          </div>
        ` : ''}
        ${lead.address ? `
          <div class="lead-contact-row">
            <a href="https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(lead.address)}" target="_blank" class="lead-contact-link">
              <span class="lead-contact-icon">\uD83D\uDCCD</span>
              <span class="lead-contact-text">${esc(lead.address)}</span>
            </a>
          </div>
        ` : ''}
        <div class="lead-contact-row" style="color:var(--gray-400);font-size:0.78rem;">
          Last contacted: ${contacted}
        </div>
      </div>
    </div>

    <!-- Quick Actions -->
    <div class="quick-actions lead-detail-actions" id="lead-quick-actions">
      ${lead.phone ? `<a href="tel:${esc(lead.phone)}" class="quick-action"><span class="qa-icon">\uD83D\uDCDE</span>Call</a>` : ''}
      ${lead.phone ? `<a href="sms:${esc(lead.phone)}" class="quick-action"><span class="qa-icon">\u2709\uFE0F</span>Text</a>` : ''}
      ${lead.address ? `<a href="https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(lead.address)}" target="_blank" class="quick-action"><span class="qa-icon">\uD83D\uDCCD</span>Directions</a>` : ''}
    </div>

    <!-- Status -->
    <div class="detail-section">
      <div class="detail-section-header">Status</div>
      <div class="detail-section-content">
        <select id="lead-status-select" class="lead-status-select">
          ${Object.entries(STATUS_OPTIONS).map(([k, v]) =>
            `<option value="${k}"${lead.status === k ? ' selected' : ''}>${v.label}</option>`
          ).join('')}
        </select>
      </div>
    </div>

    <!-- Source -->
    <div class="detail-section">
      <div class="detail-section-header">Source</div>
      <div class="detail-section-content">
        <select id="lead-source-select">
          <option value="">-- none --</option>
          ${SOURCE_OPTIONS.map(s =>
            `<option value="${s}"${lead.source === s ? ' selected' : ''}>${s}</option>`
          ).join('')}
        </select>
        ${lead.sourceDetail ? `<div class="text-muted text-sm" style="margin-top:4px;">${esc(lead.sourceDetail)}</div>` : ''}
      </div>
    </div>

    <!-- Value -->
    <div class="detail-section">
      <div class="detail-section-header">Estimated Value</div>
      <div class="detail-section-content">
        <div style="display:flex;align-items:center;gap:4px;">
          <span style="color:var(--gold);font-weight:600;">$</span>
          <input type="number" id="lead-value-input" value="${lead.value || ''}" placeholder="0" style="max-width:160px;" />
        </div>
      </div>
    </div>

    <!-- Actions Checklist -->
    <div class="detail-section">
      <div class="detail-section-header">Actions Taken</div>
      <div class="detail-section-content">
        <div class="lead-actions-grid" id="lead-actions-grid">
          ${ACTION_OPTIONS.map(a => {
            const checked = lead.actions && lead.actions.indexOf(a.key) >= 0;
            return `
              <label class="lead-action-item">
                <input type="checkbox" ${checked ? 'checked' : ''} data-action-key="${a.key}" />
                <span>${a.label}</span>
              </label>
            `;
          }).join('')}
        </div>
      </div>
    </div>

    <!-- Notes -->
    <div class="detail-section">
      <div class="detail-section-header">
        Notes (${notesLines.length})
        <button class="btn btn-sm btn-outline" id="add-note-btn">+ Add</button>
      </div>
      <div class="detail-section-content" id="notes-list">
        ${notesLines.length > 0
          ? notesLines.map(n => `<div class="note-item"><div class="note-text">${esc(n)}</div></div>`).join('')
          : '<div class="text-muted text-sm">No notes yet</div>'
        }
      </div>
    </div>

    <!-- Activity Timeline -->
    ${activities.length > 0 ? `
    <div class="detail-section">
      <div class="detail-section-header">Activity</div>
      <div class="detail-section-content">
        ${activities.slice(0, 20).map(a => renderActivity(a)).join('')}
      </div>
    </div>
    ` : ''}

    <!-- Bottom Actions -->
    <div class="detail-section">
      <div class="detail-section-content flex-col gap-sm">
        ${!lead.inPipeline ? `<button class="btn btn-gold btn-full" id="move-pipeline-btn">\u2192 Move to Sales Pipeline</button>` : ''}
        <button class="btn btn-danger btn-full" id="delete-lead-btn">\uD83D\uDDD1 Delete Lead</button>
      </div>
    </div>
  `;
}

function renderActivity(activity) {
  const time = formatDate(activity.at);
  let text = '';

  if (activity.type === 'status_change') {
    const from = (STATUS_OPTIONS[activity.from] || {}).label || activity.from;
    const to = (STATUS_OPTIONS[activity.to] || {}).label || activity.to;
    text = `Status: ${from} \u2192 ${to}`;
  } else if (activity.type === 'note') {
    text = `Note: ${activity.text}`;
  } else if (activity.type === 'action_toggle') {
    const opt = ACTION_OPTIONS.find(a => a.key === activity.action);
    text = activity.done
      ? `\u2705 ${opt ? opt.label : activity.action}`
      : `\u274C Undid: ${opt ? opt.label : activity.action}`;
  } else {
    text = activity.type || 'Activity';
  }

  return `
    <div class="lead-activity-item">
      <div class="lead-activity-text">${esc(text)}</div>
      <div class="lead-activity-time">${time}</div>
    </div>
  `;
}

function mount({ leadId }) {
  // Back button
  document.getElementById('detail-back')?.addEventListener('click', () => pop());

  // Status change
  document.getElementById('lead-status-select')?.addEventListener('change', (e) => {
    updateLeadStatus(leadId, e.target.value);
    toast('Status updated', 'success');
  });

  // Source change
  document.getElementById('lead-source-select')?.addEventListener('change', (e) => {
    updateLead(leadId, { source: e.target.value });
    toast('Source updated', 'success');
  });

  // Value change
  const valueInput = document.getElementById('lead-value-input');
  if (valueInput) {
    let valueTimer = null;
    valueInput.addEventListener('input', () => {
      if (valueTimer) clearTimeout(valueTimer);
      valueTimer = setTimeout(() => {
        updateLead(leadId, { value: Number(valueInput.value) || 0 });
      }, 800);
    });
  }

  // Action checkboxes
  const actionsGrid = document.getElementById('lead-actions-grid');
  if (actionsGrid) {
    actionsGrid.addEventListener('change', (e) => {
      const cb = e.target.closest('input[type="checkbox"]');
      if (!cb) return;
      toggleLeadAction(leadId, cb.dataset.actionKey);
      toast(cb.checked ? 'Action marked' : 'Action unmarked', 'success');
    });
  }

  // Add note
  document.getElementById('add-note-btn')?.addEventListener('click', () => {
    showModal('Add Note', `
      <div class="form-group">
        <textarea id="new-note-text" rows="3" placeholder="Enter note..."></textarea>
      </div>
      <div class="form-group">
        <label>Note Type</label>
        <select id="new-note-type">
          <option value="general">General</option>
          <option value="phone">Phone Call</option>
          <option value="field">Field Visit</option>
        </select>
      </div>
    `, [
      { label: 'Cancel', onClick: closeModal },
      { label: 'Save', type: 'gold', onClick: () => {
        const text = document.getElementById('new-note-text')?.value?.trim();
        const type = document.getElementById('new-note-type')?.value || 'general';
        if (!text) return;
        addLeadNote(leadId, text, type);
        closeModal();
        toast('Note added', 'success');
        rerender(leadId);
      }},
    ]);
    setTimeout(() => document.getElementById('new-note-text')?.focus(), 200);
  });

  // Move to pipeline
  document.getElementById('move-pipeline-btn')?.addEventListener('click', () => {
    showModal('Move to Pipeline', `<p>Move this lead to the Sales Pipeline?</p>`, [
      { label: 'Cancel', onClick: closeModal },
      { label: 'Move', type: 'gold', onClick: () => {
        const success = moveLeadToPipeline(leadId);
        closeModal();
        if (success) {
          toast('Moved to pipeline', 'success');
          rerender(leadId);
        } else {
          toast('Failed to move', 'error');
        }
      }},
    ]);
  });

  // Delete lead
  document.getElementById('delete-lead-btn')?.addEventListener('click', () => {
    showModal('Delete Lead', `<p style="color:var(--red);">This cannot be undone. Delete this lead?</p>`, [
      { label: 'Cancel', onClick: closeModal },
      { label: 'Delete', type: 'danger', onClick: () => {
        deleteLead(leadId);
        closeModal();
        toast('Lead deleted', 'success');
        pop();
      }},
    ]);
  });
}

function rerender(leadId) {
  const container = document.getElementById('view-container');
  if (container) {
    container.innerHTML = render({ leadId });
    mount({ leadId });
  }
}

function unmount() {}

function esc(str) {
  if (!str) return '';
  return String(str).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
}

function formatDate(d) {
  if (!d) return '';
  try {
    return new Date(d).toLocaleDateString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });
  } catch (e) { return ''; }
}

export default { render, mount, unmount };
